<template>
    <div class="call-player call-player-limit-width" :class="{ 'player-fullscreen': fullScreen }"
        @mouseenter="interactionEnter" @mouseleave="interactionLeave" @mousemove="playerMouseMove"
        @mouseup="playerMouseUp" @touchmove.passive="playerMouseMove" @touchend="playerMouseUp" @click="playerClick">
        <video v-show="videoReady" class="call-player-video" :class="{ 'hidden': room === 'room-page' }" playsinline
            muted autoplay></video>
        <ComponentLoader v-if="video && !videoReady"></ComponentLoader>
        <div v-if="!video && image" class="call-player-image-container">
            <div class="call-player-anim-circle1" v-if="audio && !modmuted && hasAudio"></div>
            <div class="call-player-anim-circle2" v-if="audio && !modmuted && hasAudio"></div>
            <img :src="image" class="call-player-user-image" :alt="name" />
        </div>
        <div v-if="!video && !image" class="call-player-image-container">
            <div class="call-player-anim-circle1" v-if="audio && !modmuted && hasAudio"></div>
            <div class="call-player-anim-circle2" v-if="audio && !modmuted && hasAudio"></div>
            <div class="call-player-placeholder"><span>{{ getPlaceholderByName(name) }}</span></div>
        </div>
        <div v-if="audio && !video && !modmuted && hasAudio" class="class-player-audio-intensity-container">
            <div class="class-player-audio-intensity-icon"><i class="fas fa-microphone"></i></div>
            <div class="class-player-audio-intensity-bar">
                <div class="bar-current"
                    :style="{ height: getVolumeIntensityHeight(audioIntensity), background: getVolumeIntensityColor(audioIntensity) }">
                </div>
            </div>
        </div>
        <i v-if="hand" class="fas fa-hand-paper hand-icon" :title="$t('Hand raised')"></i>
        <div class="call-player-name">
            <span v-if="modmuted" class="call-player-muted-icon"><i class="fas fa-volume-mute"></i></span>
            <span v-if="!modmuted && !audio" class="call-player-noaudio-icon"><i
                    class="fas fa-microphone-slash"></i></span>
            <span>&nbsp;{{ name }}</span>
        </div>
        <div v-if="showControls" class="call-player-controls">
            <div v-if="!self" class="call-player-controls-left">
                <button type="button" :title="renderVolumeTitle(muted, volume)"
                    class="call-player-btn call-player-volume-btn" @click.stop="clickOnVolumeIcon">
                    <i v-if="!muted && volume > 0.5" class="fas fa-volume-up"></i>
                    <i v-if="!muted && volume <= 0" class="fas fa-volume-off"></i>
                    <i v-if="!muted && volume > 0 && volume <= 0.5" class="fas fa-volume-down"></i>
                    <i v-if="muted" class="fas fa-volume-mute"></i>
                </button>
                <div class="player-volume-btn-expand" @mousedown.stop="grabVolume" @touchstart.passive.stop="grabVolume" @click.stop>
                    <div class="player-volume-container">
                        <div class="player-volume-bar"></div>
                        <div class="player-volume-current" :style="{ width: getVolumeBarWidth() }"></div>
                        <div class="player-volume-thumb" :style="{ left: getVolumeThumbLeft() }"></div>
                    </div>
                </div>
            </div>
            <div class="call-player-controls-right">
                <button v-if="adminopts && !self" type="button" :title="$t('Options')"
                    class="call-player-btn call-player-config-btn" @click.stop="onClickOptions"><i
                        class="fas fa-bars"></i></button>
                <button type="button" :title="$t('Toggle full screen')" class="call-player-btn call-player-expand-btn"
                    @click.stop="toggleFullScreen()"><i v-if="!fullScreen" class="fas fa-expand"></i><i v-if="fullScreen"
                        class="fas fa-compress"></i></button>
            </div>
        </div>

        <div class="call-player-options" v-if="showControls && showOptions" @click.stop>
            <div class="player-config-button" v-if="!modmuted" @click="doAction('mod-mute')">
                <i class="fas fa-volume-mute player-config-button-check"></i>
                <b>{{ $t("Mute user") }}</b>
            </div>
            <div class="player-config-button" v-if="modmuted" @click="doAction('mod-unmute')">
                <i class="fas fa-volume-up player-config-button-check"></i>
                <b>{{ $t("Unmute user") }}</b>
            </div>
            <div v-if="!self" class="player-config-button delete-color" @click="doAction('mod-kick')">
                <i class="fas fa-phone-slash player-config-button-check"></i>
                <b>{{ $t("Kick from call") }}</b>
            </div>
            <div v-if="!self" class="player-config-button delete-color" @click="doAction('mod-ban')">
                <i class="fas fa-ban player-config-button-check"></i>
                <b>{{ $t("Ban user") }}</b>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent, nextTick } from "vue";

import { UserMediaController } from "@/control/user-media";
import { openFullscreen, closeFullscreen } from "@/utils/full-screen";
import ComponentLoader from "@/components/utils/ComponentLoader.vue";
import { CallsController } from "@/control/call";
import { RoomController } from "@/control/room";

export default defineComponent({
    components: {
        ComponentLoader,
    },
    name: "CallPlayer",
    setup() {
        return {
            rendererTimer: null,
            audioSource: null,
            audioContext: null,
            audioAnalyser: null,
            audioGain: null as GainNode | null,
            _resumeOnGesture: null as (() => void) | null,
            exitFullScreenListener: null,
        };
    },
    props: ['uid', 'self', 'gvolume', 'gmuted', 'streamid', 'stick', 'name', 'image', 'modmuted', 'audio', 'video', 'adminopts', 'room', 'videoAvailable', 'hand', 'volume'],
    data: function () {
        return {
            volumeVar: typeof this.volume === "number" ? this.volume : 1,
            globalVolume: typeof this.gvolume === "number" ? this.gvolume : 1,
            muted: false,

            showControls: false,
            volumeGrabbed: false,

            showOptions: false,

            fullScreen: false,

            supportedAudioAnalyzer: false, //TODO:!!window.AudioContext,
            hasAudio: false,
            audioIntensity: 0,
            videoReady: this.videoAvailable,
        };
    },
    methods: {
        getVolume: function (g, r) {
            if (this.room !== 'room-page') {
                return 0;
            }
            return g * r;
        },

        getPlaceholderByName: function (n: string) {
            return (n + "").substr(0, 1).toUpperCase();
        },

        doAction: function (a: string) {
            this.$emit(a, this.uid);
            this.showOptions = false;
        },

        /* Mouse events */

        onClickOptions: function (e) {
            this.showOptions = !this.showOptions;
            e.stopPropagation();
        },

        interactionEnter: function () {
            this.showControls = true;
        },

        interactionLeave: function () {
            this.showControls = false;
            this.volumeGrabbed = false;
            this.showOptions = false;
        },

        playerMouseUp: function (e) {
            if (this.volumeGrabbed) {
                if (e.touches && e.touches.length > 0) {
                    this.modifyVolumeByMouse(e.touches[0].pageX, e.touches[0].pageY);
                } else {
                    this.modifyVolumeByMouse(e.pageX, e.pageY);
                }
                this.volumeGrabbed = false;
            }

            if (this.streamid) {
                const vid = this.$el.querySelector("video");
                if (vid && vid.paused) {
                    vid.play();
                }
            }
        },

        playerMouseMove: function (e) {
            this.interactionEnter();

            if (this.volumeGrabbed) {
                if (e.touches && e.touches.length > 0) {
                    this.modifyVolumeByMouse(e.touches[0].pageX, e.touches[0].pageY);
                } else {
                    this.modifyVolumeByMouse(e.pageX, e.pageY);
                }
            }
        },

        playerClick: function () {
            this.showOptions = false;
        },

        /* Volume */

        grabVolume: function (e) {
            this.volumeGrabbed = true;
            if (e.touches && e.touches.length > 0) {
                this.modifyVolumeByMouse(e.touches[0].pageX, e.touches[0].pageY);
            } else {
                this.modifyVolumeByMouse(e.pageX, e.pageY);
            }
        },

        getActualVolume: function () {
            if (this.muted) {
                return 0;
            }
            return this.volumeVar;
        },

        renderVolumeTitle: function (muted: boolean, vol: number) {
            if (muted) {
                return this.$t("Volume") + ": " + this.$t("Muted");
            } else {
                const pt = Math.floor(vol * 100);
                return this.$t("Volume") + ": " + pt + "%";
            }
        },

        changeVolume: function (vol: number) {
            this.muted = false;
            this.volumeVar = vol;
            this._applyGain();
            RoomController.setUserVolume(this.uid, vol);
        },

        getVolumeBarWidth: function () {
            const volume = Math.min(1, Math.max(0, this.getActualVolume()));
            const width = Math.floor(65 * volume);
            return width + 'px';
        },

        getVolumeThumbLeft: function () {
            const volume = Math.min(1, Math.max(0, this.getActualVolume()));
            const width = Math.floor(65 * volume);
            return Math.floor(width) + 'px';
        },

        modifyVolumeByMouse: function (x, y) {
            if (typeof x !== "number" || typeof y !== "number" || isNaN(x) || isNaN(y)) {
                return;
            }
            const elem = this.$el.querySelector(".player-volume-bar");
            if (!elem) {
                return;
            }
            const offset = elem.getBoundingClientRect().left;
            if (x < offset) {
                this.changeVolume(0);
            } else {
                const p = x - offset;
                const vol = Math.min(1, p / 65);
                this.changeVolume(vol);
            }
        },

        clickOnVolumeIcon: function () {
            this.toggleMuted();
        },

        toggleMuted: function () {
            this.muted = !this.muted;
            this._applyGain();
            if (this.muted) {
                RoomController.setUserVolume(this.uid, 0);
            } else {
                if (this.volumeVar <= 0) {
                    this.volumeVar = 1; // Reset to default volume if it was muted
                }
                RoomController.setUserVolume(this.uid, this.volumeVar);
            }
        },

        requestStream: function () {
            if (!this.streamid) return;
            this.$emit("stream-request", this, this.streamid)
        },

        setStream: function (s) {
            const vid = this.$el.querySelector("video");

            if (vid) {
                try {
                    vid.srcObject = s;
                    // The <video> element is always muted (audio is routed
                    // through the Web Audio API to bypass autoplay policy).
                    vid.muted = true;
                    const tryPlay = () => {
                        vid.play().catch((err) => {
                            console.warn("Cannot play video:", err);
                        });
                    };
                    if (document.contains(vid) && vid.readyState >= 2) {
                        tryPlay();
                    } else {
                        vid.oncanplay = () => {
                            if (this.video) {
                                this.videoReady = true;
                                CallsController.videoAvailable(this.streamid);
                            }
                            tryPlay();
                        };
                    }
                } catch (err) {
                    console.error("Error assigning stream to video:", err);
                }

                // Recovery listeners: re-assign srcObject when tracks end, mute,
                // or get removed.  This forces the <video> element to re-evaluate
                // the stream and prevents permanent freezes.
                const reassign = () => {
                    if (!vid || !document.contains(vid)) return;
                    if (vid.srcObject !== s) return; // stale closure — a newer stream has been assigned
                    vid.srcObject = null;
                    vid.srcObject = s;
                    vid.play().catch(() => { /**/ });
                };

                s.onremovetrack = () => {
                    reassign();
                };

                s.getVideoTracks().forEach(track => {
                    track.onended = () => {
                        console.warn("[CallPlayer] Video track ended:", track.id);
                        reassign();
                    };
                    track.onmute = () => {
                        console.warn("[CallPlayer] Video track muted:", track.id);
                    };
                    track.onunmute = () => {
                        // Track resumed (e.g. replaceTrack on remote side) — kick the element
                        reassign();
                    };
                });
            }

            if (typeof s.onaddtrack === "function") {
                const original = s.onaddtrack;
                s.onaddtrack = (...args) => {
                    original(...args);
                    this.setStream(s);
                };
            } else {
                s.onaddtrack = () => {
                    this.setStream(s);
                };
            }

            this.setupAudioAnalyzer(s);
        },

        setupAudioAnalyzer: function (s) {
            this.clearAudioAnalyzer();

            if (!window.AudioContext) {
                return; // Not supported
            }

            if (!UserMediaController.checkMediaStreamAudio(s)) {
                return; // The stream does not have an audio track
            }

            this.hasAudio = true;

            const context = new AudioContext();
            const source = context.createMediaStreamSource(s);
            const analyser = context.createAnalyser();

            this.audioContext = context;
            this.audioSource = source;
            this.audioAnalyser = analyser;
            source.connect(analyser);

            // For remote participants, route audio to speakers through a
            // GainNode.  The <video> element stays permanently muted so that
            // autoplay never blocks audio — all output goes through Web Audio.
            if (!this.self) {
                const gainNode = context.createGain();
                this._applyGain(gainNode);
                source.connect(gainNode);
                gainNode.connect(context.destination);
                this.audioGain = gainNode;
            }

            // Ensure the AudioContext is running.  If the browser suspended it
            // (autoplay policy), resume now and set up a fallback listener for
            // the next user gesture.
            if (context.state !== 'running') {
                context.resume().catch(() => { /**/ });
            }
            if (context.state === 'suspended') {
                const resumeOnGesture = () => {
                    if (context.state !== 'running') {
                        context.resume().catch(() => { /**/ });
                    }
                    document.removeEventListener('click', resumeOnGesture, true);
                    document.removeEventListener('touchstart', resumeOnGesture, true);
                    this._resumeOnGesture = null;
                };
                document.addEventListener('click', resumeOnGesture, true);
                document.addEventListener('touchstart', resumeOnGesture, true);
                this._resumeOnGesture = resumeOnGesture;
            }

            analyser.fftSize = 32;

            this.rendererTimer = setInterval(
                this.audioAnimationFrame.bind(this),
                Math.floor(1000 / 25)
            );
        },

        /** Compute and apply the effective gain to the GainNode. */
        _applyGain: function (gainNode?: GainNode) {
            const g = gainNode || this.audioGain;
            if (!g) return;
            if (this.self || this.muted || this.gmuted || this.modmuted) {
                g.gain.value = 0;
            } else {
                const globalVolume = typeof this.globalVolume === "number" ? this.globalVolume : 1;
                const userVolume = typeof this.volumeVar === "number" ? this.volumeVar : 1;
                g.gain.value = globalVolume * userVolume;
            }
        },

        setAudioOutputDevice: async function (sinkId: string) {
            if (this.audioContext && typeof (this.audioContext as any).setSinkId === "function") {
                await (this.audioContext as any).setSinkId(sinkId);
            }
        },

        clearAudioAnalyzer: function () {
            this.hasAudio = false;

            if (this.rendererTimer) {
                clearInterval(this.rendererTimer);
                this.rendererTimer = null;
            }

            if (this._resumeOnGesture) {
                document.removeEventListener('click', this._resumeOnGesture, true);
                document.removeEventListener('touchstart', this._resumeOnGesture, true);
                this._resumeOnGesture = null;
            }

            if (this.audioGain) {
                this.audioGain.disconnect();
                this.audioGain = null;
            }

            if (this.audioSource && this.audioAnalyser) {
                this.audioSource.disconnect(this.audioAnalyser);
            }

            if (this.audioSource) {
                this.audioSource.disconnect();
            }

            if (this.audioContext) {
                this.audioContext.close();
            }

            this.audioContext = null;
            this.audioSource = null;
            this.audioAnalyser = null;
        },

        audioAnimationFrame: function () {
            if (!this.audio || this.video || this.modmuted) {
                return;
            }

            const analyser = this.audioAnalyser;

            if (!analyser) {
                return;
            }

            const bufferLength = analyser.frequencyBinCount;
            const dataArray = new Uint8Array(bufferLength);

            analyser.getByteFrequencyData(dataArray);

            let intensity = 0;

            for (let i = 0; i < bufferLength; i++) {
                intensity = Math.max(intensity, dataArray[i]);
            }

            this.audioIntensity = intensity;
        },

        getVolumeIntensityHeight: function (i) {
            return (Math.floor((i * 100 / 255) * 100) / 100) + "%";
        },

        getVolumeIntensityColor: function (i) {
            const r = i;
            const g = 255 - i;
            const b = 50;
            return "rgb(" + r + "," + g + "," + b + ")";
        },

        /* Full screen */

        toggleFullScreen: function () {
            this.fullScreen = !this.fullScreen;
            if (this.fullScreen) {
                openFullscreen(this.$el);
            } else {
                closeFullscreen();
            }
        },

        onExitFullScreen: function () {
            if (!document.fullscreenElement) {
                this.fullScreen = false;
            }
        },
        onUserVolumeChange: function (uid, volume) {
            if (uid === this.uid) {
                this.volumeVar = volume;
                this._applyGain();
            }
        },
        onVolumeChange: function (volume) {
            this.globalVolume = volume;
            this._applyGain();
        },
    },
    mounted: function () {
        this.exitFullScreenListener = this.onExitFullScreen.bind(this);

        document.addEventListener('fullscreenchange', this.exitFullScreenListener);
        document.addEventListener('webkitfullscreenchange', this.exitFullScreenListener);
        document.addEventListener('mozfullscreenchange', this.exitFullScreenListener);
        document.addEventListener('MSFullscreenChange', this.exitFullScreenListener);

        this.$options.userVolumeH = this.onUserVolumeChange.bind(this);
        this.$options.globalVolumeH = this.onVolumeChange.bind(this);
        RoomController.on("volume-changed", this.$options.globalVolumeH);
        RoomController.on("user-volume-changed", this.$options.userVolumeH);


        nextTick(this.requestStream.bind(this));
    },
    beforeUnmount: function () {
        this.clearAudioAnalyzer();
        document.removeEventListener('fullscreenchange', this.exitFullScreenListener);
        document.removeEventListener('webkitfullscreenchange', this.exitFullScreenListener);
        document.removeEventListener('mozfullscreenchange', this.exitFullScreenListener);
        document.removeEventListener('MSFullscreenChange', this.exitFullScreenListener);

        RoomController.removeEventListener("volume-changed",this.$options.globalVolumeH);
        RoomController.removeEventListener("user-volume-changed", this.$options.userVolumeH);
    },
    watch: {
        stick: function () {
            this.requestStream();
        },
        videoAvailable: function () {
            this.videoReady = this.videoAvailable;
        },
        gmuted: function () {
            this._applyGain();
        },
        modmuted: function () {
            this._applyGain();
        },
    },
});
</script>

<style scoped>
@import "@/style/routes/call-players.css";
</style>
